import { forwardRef, type KeyboardEvent } from 'react'

import { CommandInputFieldStyled } from '../CommandInput'

/**
 * Headless combobox half of the WAI-ARIA 1.2 combobox/listbox pattern.
 * Pairs with GlobalSearchListbox. Owns focus and handles keyboard navigation
 * (ArrowUp/Down, Enter) via aria-activedescendant. Focus never moves to the listbox.
 */
interface IGlobalSearchComboboxProps {
  listboxId: string
  query: string
  activeDescendantId: string | undefined
  isExpanded: boolean
  placeholder?: string
  ariaLabel?: string
  onQueryChange: (value: string) => void
  onEnter: () => void
  onNavigateDown: () => void
  onNavigateUp: () => void
}

export const GlobalSearchCombobox = forwardRef<
  HTMLInputElement,
  IGlobalSearchComboboxProps
>(
  (
    {
      listboxId,
      query,
      activeDescendantId,
      isExpanded,
      placeholder,
      ariaLabel,
      onQueryChange,
      onEnter,
      onNavigateDown,
      onNavigateUp,
    },
    ref
  ) => {
    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          onNavigateDown()
          break
        case 'ArrowUp':
          e.preventDefault()
          onNavigateUp()
          break
        case 'Enter':
          if (!e.nativeEvent.isComposing) {
            onEnter()
            e.preventDefault()
          }
          break
        default:
          break
      }
    }

    return (
      <CommandInputFieldStyled
        ref={ref}
        data-analytics-id='global-search-input'
        type='text'
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        aria-label={ariaLabel}
        role='combobox'
        aria-expanded={isExpanded}
        aria-controls={listboxId}
        aria-haspopup='listbox'
        aria-autocomplete='list'
        aria-activedescendant={activeDescendantId}
      />
    )
  }
)

GlobalSearchCombobox.displayName = 'GlobalSearchCombobox'
